/**
 * CreditBadge Component
 * Shows current credit balance in the sidebar/header.
 * Returns null when credits are disabled (enterprise deployments).
 */

import React from 'react';
import { useNavigate } from 'react-router';
import { Coins } from 'lucide-react';
import { cn } from '../ui/utils';
import { useBilling } from '../../contexts/BillingContext';
import { useTranslation } from '../../i18n';
import { ROUTES } from '../../router';

function formatCredits(value: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
}

function getColorClasses(available: number): string {
  if (available <= 0) {
    return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400';
  }
  if (available < 2000) {
    return 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-500/20 text-amber-700 dark:text-amber-400';
  }
  return 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-500/20 text-emerald-700 dark:text-emerald-400';
}

export function CreditBadge() {
  const { creditsEnabled, balance, isLoading } = useBilling();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Enterprise / credits disabled — render nothing
  if (!creditsEnabled) return null;

  // Loading skeleton
  if (isLoading || balance === null) {
    return (
      <div className="h-8 w-full rounded-lg bg-gray-200 dark:bg-white/10 animate-pulse" />
    );
  }

  const available = balance.available;

  const label =
    available <= 0
      ? t('billing.credits_depleted')
      : available < 2000
      ? t('billing.credits_low')
      : t('billing.credits_available', { count: formatCredits(available) });

  return (
    <button
      onClick={() => navigate(ROUTES.BILLING)}
      className={cn(
        'w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all duration-200 hover:opacity-80',
        getColorClasses(available)
      )}
      title={t('billing.credits_available', { count: formatCredits(available) })}
    >
      <Coins className="w-3.5 h-3.5 shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  );
}
